/// Parts of a sail + points of a sail — the RYA Day Skipper / Yachtmaster
/// vocabulary every sailing student needs by name.
///
/// **Parts** are the named edges, corners and fittings on a sail and
/// its running rigging. We model the Bermudan mainsail (the most
/// common modern rig) but the same vocabulary applies to most jibs
/// and genoas — only the "tack" and "head" fittings change.
///
/// **Points of sail** are the boat's heading relative to the true
/// wind: in irons (no-go zone), close-hauled, close reach, beam
/// reach, broad reach, and run. Plus the two transitions across the
/// wind: tacking (head through the wind) and gybing (stern through).

export type SailKind = "main" | "jib";

export interface SailPart {
  /// Stable id used by the recognition flashcards game.
  id: string;
  /// Display name shown in the reveal and chosen as the multi-choice
  /// answer in the recognition drill.
  name: string;
  /// One-line plain-English meaning for the reveal panel.
  meaning: string;
  /// Position on a 100 × 100 SVG figure of a Bermudan mainsail —
  /// drives both the labelled diagram in the Reference panel and the
  /// "click the part" mode of the game. (0, 0) is the top-left of
  /// the viewbox, the mast runs up the left side, the boom along
  /// the bottom.
  x: number;
  y: number;
  /// Which sails this part appears on. A jib and a main share head /
  /// tack / clew / luff / leech / foot, but battens, reef points
  /// and the kicking strap are main-only.
  appearsOn: readonly SailKind[];
  /// Operational "why" the student remembers it for.
  use: string;
}

/// Bermudan mainsail laid out in the 100 × 100 viewbox. Mast at x ≈
/// 18 from y = 14 (head) down to y = 90 (gooseneck); boom from
/// x = 18, y = 90 out to x = 86, y = 92. Sail fills the triangle.
export const SAIL_PARTS: readonly SailPart[] = Object.freeze([
  {
    id: "head",
    name: "Head",
    meaning: "Top corner of the sail — the halyard attaches here.",
    x: 22,
    y: 14,
    appearsOn: ["main", "jib"],
    use: "Where you hoist from; the halyard shackle clips on at the head.",
  },
  {
    id: "tack",
    name: "Tack",
    meaning: "Forward bottom corner — at the gooseneck on a main, the foredeck on a headsail.",
    x: 22,
    y: 90,
    appearsOn: ["main", "jib"],
    use: "Anchors the sail's forward bottom corner; the tackline or shackle holds it down.",
  },
  {
    id: "clew",
    name: "Clew",
    meaning: "Aft bottom corner — the sheet is attached here.",
    x: 86,
    y: 90,
    appearsOn: ["main", "jib"],
    use: "The mainsheet pulls the boom (and so the clew) in and out; the jib sheets lead from the clew aft to the cockpit.",
  },
  {
    id: "luff",
    name: "Luff",
    meaning: "Leading edge of the sail — runs along the mast (main) or the forestay (jib).",
    x: 20,
    y: 50,
    appearsOn: ["main", "jib"],
    use: "Where the wind hits first. When the luff lifts / flutters you're pinching too close to the wind.",
  },
  {
    id: "leech",
    name: "Leech",
    meaning: "Trailing edge of the sail — runs from head to clew.",
    x: 56,
    y: 52,
    appearsOn: ["main", "jib"],
    use: "Carries telltales at each batten. Twist in the leech is the open / closed top of the sail.",
  },
  {
    id: "foot",
    name: "Foot",
    meaning: "Bottom edge of the sail — runs along the boom (main) or in free air (loose-footed sails).",
    x: 54,
    y: 92,
    appearsOn: ["main", "jib"],
    use: "Set up by the outhaul: tight foot for upwind power, eased foot for off-wind belly.",
  },
  {
    id: "batten",
    name: "Batten",
    meaning: "Stiffening rod inserted across the leech to hold sail shape.",
    x: 48,
    y: 40,
    appearsOn: ["main"],
    use: "Supports the roach (the curve outside a straight head-to-clew line). Without battens the leech flutters and the roach collapses.",
  },
  {
    id: "telltale",
    name: "Telltale",
    meaning: "Small ribbon of wool or nylon on the luff or leech that shows airflow.",
    x: 30,
    y: 38,
    appearsOn: ["main", "jib"],
    use: "Trim until both telltales (windward + leeward) stream aft horizontally. If the windward telltale lifts, you're pinching.",
  },
  {
    id: "reef-point",
    name: "Reef point",
    meaning: "Row of grommets used to tie down a reefed portion of the sail.",
    x: 50,
    y: 78,
    appearsOn: ["main"],
    use: "When the wind gets up you drop the sail to the next reef cringle and tie the bunt to the boom with the reef points — slab reefing.",
  },
  {
    id: "cringle",
    name: "Cringle",
    meaning: "Reinforced ring at a reef position on the luff and leech.",
    x: 24,
    y: 72,
    appearsOn: ["main"],
    use: "The reefing cringles take the load of the reefing line and reef-hook — the luff cringle hooks on at the gooseneck.",
  },
  {
    id: "boom",
    name: "Boom",
    meaning: "Horizontal spar along the foot of the mainsail.",
    x: 50,
    y: 95,
    appearsOn: ["main"],
    use: "Carries the foot of the main; the mainsheet, kicker and outhaul all bear on it. Watch your head during tacks and gybes.",
  },
  {
    id: "mast",
    name: "Mast",
    meaning: "Vertical spar carrying the sails.",
    x: 16,
    y: 50,
    appearsOn: ["main"],
    use: "The luff of the main slides up a track or bolt-rope on the mast; halyards and shrouds anchor at the masthead.",
  },
  {
    id: "kicker",
    name: "Kicker / vang",
    meaning: "Tackle from the boom down to the mast base, controlling boom height.",
    x: 26,
    y: 95,
    appearsOn: ["main"],
    use: "Stops the boom rising when the sheet is eased off-wind, holding the leech twist under control.",
  },
  {
    id: "outhaul",
    name: "Outhaul",
    meaning: "Line that pulls the clew aft along the boom, flattening the foot.",
    x: 80,
    y: 96,
    appearsOn: ["main"],
    use: "Tension the outhaul upwind for a flat sail; ease it off-wind for more belly and power.",
  },
  {
    id: "halyard",
    name: "Halyard",
    meaning: "Line that hoists the sail — runs from the head, up to the masthead and back down.",
    x: 18,
    y: 24,
    appearsOn: ["main", "jib"],
    use: "Tension sets the luff: hard-up gives a fast, flat sail; eased lets the draft move aft for power in light airs.",
  },
  {
    id: "sheet",
    name: "Sheet",
    meaning: "Line attached at the clew that controls the sail's trim across the boat.",
    x: 90,
    y: 95,
    appearsOn: ["main", "jib"],
    use: "Trim sheet in for upwind, ease out as you bear away. The mainsheet runs from the boom; jib sheets run from the clew on each side.",
  },
]);

export const SAIL_PART_IDS: readonly string[] = SAIL_PARTS.map((p) => p.id);

/// ───────────────────────────────────────────────────────────────────
/// Points of sail
/// ───────────────────────────────────────────────────────────────────

export type PointOfSail =
  | "in-irons"
  | "close-hauled"
  | "close-reach"
  | "beam-reach"
  | "broad-reach"
  | "run";

export interface PointInfo {
  id: PointOfSail;
  name: string;
  /// Mid-angle of the boat's heading relative to the true wind, in
  /// degrees. 0° = pointing directly into the wind ("in irons").
  /// 180° = pointing directly downwind ("run"). Used to draw the
  /// boat on the points-of-sail compass and to score the picker game.
  midAngleDeg: number;
  /// Half-width of the sector around `midAngleDeg`. A heading within
  /// `±halfWidthDeg` of `midAngleDeg` counts as this point of sail.
  halfWidthDeg: number;
  /// One-line summary the student sees in the reveal.
  summary: string;
  /// Sail trim cue used in the reveal panel.
  trim: string;
  /// Telltale-level "why" — when to choose this heading.
  notes: string;
}

/// The six points of sail with the conventional RYA sector angles.
/// The "no-go zone" runs roughly ±45° either side of the wind on a
/// modern Bermudan rig; closer than that and the sails luff.
export const POINTS_OF_SAIL: readonly PointInfo[] = Object.freeze([
  {
    id: "in-irons",
    name: "In irons (no-go zone)",
    midAngleDeg: 0,
    halfWidthDeg: 22,
    summary: "Heading too close to the wind for the sails to fill — boat slows, stops, drifts backwards.",
    trim: "Sails are flapping/luffing. The only fix is to bear away and re-build boat speed before settling on a heading.",
    notes: "Happens when you over-tack or pinch too high. To recover, push the tiller to leeward to back the jib and pivot the bow off the wind.",
  },
  {
    id: "close-hauled",
    name: "Close-hauled",
    midAngleDeg: 45,
    halfWidthDeg: 10,
    summary: "Sailing as close to the wind as the sails will allow — typically 40-50° off the true wind.",
    trim: "Sheets hardened in. Telltales streaming aft on both sides. Boat heels; helm carries a touch of weather helm.",
    notes: "The slow, high-VMG point. To reach a windward destination you tack the boat through ~90° between port and starboard close-hauled.",
  },
  {
    id: "close-reach",
    name: "Close reach",
    midAngleDeg: 70,
    halfWidthDeg: 12,
    summary: "Sails slightly eased — heading between close-hauled and the beam.",
    trim: "Ease the sheets a hand's width from close-hauled until the windward telltales just stop lifting.",
    notes: "Comfortable, fast point of sail — often the quickest way to a destination roughly upwind without pinching.",
  },
  {
    id: "beam-reach",
    name: "Beam reach",
    midAngleDeg: 90,
    halfWidthDeg: 15,
    summary: "Wind on the beam — perpendicular to the boat's heading.",
    trim: "Sheets eased to roughly mid-traveller. Boom around 45° off centreline. Sail full of wind without luffing.",
    notes: "The fastest point of sail for most yachts. The benchmark for the figure-of-eight MOB return.",
  },
  {
    id: "broad-reach",
    name: "Broad reach",
    midAngleDeg: 135,
    halfWidthDeg: 22,
    summary: "Wind on the aft quarter — between beam and dead-downwind.",
    trim: "Sheets well out. The kicker stops the boom rising. Apparent wind drops noticeably.",
    notes: "Comfortable downwind point. Watch for an unexpected gybe if the boat rolls or the wind shifts — preventer rigged.",
  },
  {
    id: "run",
    name: "Run",
    midAngleDeg: 180,
    halfWidthDeg: 22,
    summary: "Sailing dead downwind with the wind directly astern.",
    trim: "Boom right out, possibly on a preventer. Genoa goosewinged on a pole, or main blanketing the jib.",
    notes: "Slow apparent wind, easy to get caught by an accidental gybe. RYA practice: avoid a pure run by 'tacking downwind' on broad reaches when there's room.",
  },
]);

/// Identify which point of sail a given boat heading (in degrees off
/// the true wind, 0..180, port or starboard symmetric) corresponds to.
export function pointForAngle(angleOffWindDeg: number): PointOfSail {
  const a = Math.abs(angleOffWindDeg);
  // Walk the points in order; return the first sector that contains
  // `a`. Sectors are contiguous and cover 0..180.
  for (const p of POINTS_OF_SAIL) {
    if (a <= p.midAngleDeg + p.halfWidthDeg) return p.id;
  }
  return "run";
}

/// ───────────────────────────────────────────────────────────────────
/// WOBBLE — the RYA pre-engine-start mnemonic.
/// ───────────────────────────────────────────────────────────────────

export interface WobbleStep {
  /// Letter of the mnemonic (W, O, B, B, L, E). The duplicated B is
  /// kept distinct via the `id` field.
  letter: "W" | "O" | "B" | "L" | "E";
  /// Stable id for the recognition drill (`water`, `oil`,
  /// `belts`, `bilges`, `leaks`, `exhaust`).
  id: "water" | "oil" | "belts" | "bilges" | "leaks" | "exhaust";
  /// Short name of the check.
  name: string;
  /// What the student actually checks for.
  detail: string;
  /// One-line "why" — what fails if you skip this check.
  why: string;
}

/// The classic six-step RYA daily-engine mnemonic. Done EVERY day
/// before you turn the key — saves a callout and saves an engine.
///
/// Note: a few RYA centres teach "Battery" instead of "Bilges" for
/// the second B. We list the bilges version as the primary (it
/// catches more failure modes — bilge water often signals a stern-
/// gland or stuffing-box leak) and mention battery as a "while
/// you're there" extra in the panel text.
export const WOBBLE_STEPS: readonly WobbleStep[] = Object.freeze([
  {
    letter: "W",
    id: "water",
    name: "Water",
    detail:
      "Coolant level in the header tank (open the cap COLD only) and raw-water sea-cock open + strainer clear.",
    why: "No coolant or a blocked strainer → overheat in minutes, head gasket gone in twenty.",
  },
  {
    letter: "O",
    id: "oil",
    name: "Oil",
    detail:
      "Engine oil on the dipstick within marks; gearbox oil dipstick or sight glass within marks.",
    why: "Run dry and the engine seizes — the most expensive five-minute mistake on the boat.",
  },
  {
    letter: "B",
    id: "belts",
    name: "Belts",
    detail:
      "Alternator and water-pump belts taut (deflect ~10 mm on a thumb push), no fraying or polished glazing.",
    why: "Belt snaps → no charging AND no raw-water cooling → engine overheats within minutes.",
  },
  {
    letter: "B",
    id: "bilges",
    name: "Bilges",
    detail:
      "Bilge dry under the engine. A film of clean water is normal; a slick of oil, fuel, or rising water is not.",
    why: "Water in the bilge means a leak — stern gland, hose clip, raw-water pump — that gets worse under load.",
  },
  {
    letter: "L",
    id: "leaks",
    name: "Leaks",
    detail:
      "Look over the engine — fuel lines, raw-water hoses, exhaust elbow, coolant hoses. Any drip, weep, or fresh stain.",
    why: "Fuel weeping near a hot exhaust elbow is the start of a fire; a tiny coolant drip means the system can't hold pressure.",
  },
  {
    letter: "E",
    id: "exhaust",
    name: "Exhaust (once started)",
    detail:
      "Within 30 seconds of starting, confirm raw water is flowing out of the exhaust outlet.",
    why: "No water out → raw-water pump impeller has failed → overheat alarm will go off before you've cleared the marina.",
  },
]);

export const WOBBLE_IDS: readonly string[] = WOBBLE_STEPS.map((s) => s.id);

/// ───────────────────────────────────────────────────────────────────
/// Sail-handling sequences — the call-and-response procedures every
/// RYA student is examined on (NSS L2+, Competent Crew, Day Skipper).
/// ───────────────────────────────────────────────────────────────────

export type ManoeuvreId =
  | "tacking"
  | "gybing"
  | "reefing"
  | "heaving-to"
  | "capsize-recovery";

export type StepActor = "helm" | "crew" | "both";

export interface ManoeuvreStep {
  /// Who is doing this step.
  actor: StepActor;
  /// Verbal call-out, if any (helmsman's lead-in or crew's response).
  /// Empty string for silent actions.
  callout: string;
  /// What the actor physically does at this step.
  action: string;
  /// Optional "why" — what fails if this step is skipped or done
  /// out of order.
  reason?: string;
}

export interface SailManoeuvre {
  id: ManoeuvreId;
  /// Short title used in the Reference panel and the game.
  name: string;
  /// One-line summary.
  summary: string;
  /// When in a sail you reach for this manoeuvre.
  whenToUse: string;
  /// Step-by-step procedure in the order the crew executes it.
  steps: readonly ManoeuvreStep[];
  /// Common mistakes / failure modes — used in the reveal text.
  pitfalls: readonly string[];
  /// Course level where this is first taught.
  course: "NSS L1" | "NSS L2" | "NSS L3" | "Comp Crew" | "Day Skipper";
}

export const SAIL_MANOEUVRES: readonly SailManoeuvre[] = Object.freeze([
  {
    id: "tacking",
    name: "Tacking",
    summary:
      "Turn the bow through the wind to put the boat on the opposite tack — the standard upwind manoeuvre.",
    whenToUse:
      "Sailing upwind to a destination roughly upwind of you. You can't sail directly into the wind, so you tack between port and starboard close-hauled.",
    steps: [
      {
        actor: "helm",
        callout: "Ready about!",
        action: "Look to windward + leeward, check water is clear, alert the crew.",
        reason: "If a vessel is bearing down or the area is crowded, abort.",
      },
      {
        actor: "crew",
        callout: "Ready!",
        action: "Take working jib sheet in hand, ready the lazy sheet on the new side. Crew weight to new windward.",
      },
      {
        actor: "helm",
        callout: "Lee-oh!",
        action: "Push the tiller smoothly to leeward (or pull the wheel up).",
        reason: "Smooth turn preserves boat speed — fast turn stalls and you get stuck head-to-wind.",
      },
      {
        actor: "crew",
        callout: "",
        action:
          "As the bow passes through the wind, release the old working jib sheet and haul the new sheet in fast.",
        reason: "Late release strangles the bow; late haul leaves the jib flogging.",
      },
      {
        actor: "both",
        callout: "",
        action:
          "Helm centres the rudder on the new close-hauled course; crew finishes trimming the jib + main; crew weight settles to new windward.",
      },
    ],
    pitfalls: [
      "Calling 'Lee-oh' before the crew is ready — they're caught with the wrong sheet in hand.",
      "Turning too slowly: boat ends up in irons (stuck head-to-wind, no steerage way).",
      "Turning too fast: boat heels and stalls before completing the tack.",
      "Releasing the old jib sheet too early — boat backs against the jib and pivots back.",
    ],
    course: "NSS L2",
  },
  {
    id: "gybing",
    name: "Gybing (controlled)",
    summary:
      "Turn the stern through the wind — the downwind opposite of tacking, with the boom crossing the boat.",
    whenToUse:
      "Changing tack while sailing downwind. The wind is behind you so the boom is well out; the controlled gybe brings it across with the mainsheet hauled in to limit the swing.",
    steps: [
      {
        actor: "helm",
        callout: "Stand by to gybe!",
        action: "Check astern and to windward for traffic; brief the crew on the new course.",
      },
      {
        actor: "crew",
        callout: "Ready!",
        action:
          "Take the mainsheet in hand (and the kicker), prepare new jib sheets. Heads down — the boom will swing.",
      },
      {
        actor: "crew",
        callout: "",
        action: "Haul the mainsheet hard in until the boom is on the centreline.",
        reason: "Centring the boom limits how far it swings — the difference between a controlled gybe and a flogging crash.",
      },
      {
        actor: "helm",
        callout: "Gybe-oh!",
        action: "Push the tiller to windward (or pull wheel down). The boat turns the stern through the wind.",
      },
      {
        actor: "crew",
        callout: "",
        action:
          "As the boom crosses, ease the mainsheet out smoothly to the new side. Release old jib sheet, haul new one.",
      },
      {
        actor: "both",
        callout: "",
        action: "Settle on the new course; trim sails for the new heading.",
      },
    ],
    pitfalls: [
      "Crew not warned + boom catches a head: serious injury risk. Always call early and call clearly.",
      "Mainsheet not centred before the gybe: the boom swings unchecked and either breaks something or capsizes the boat.",
      "Bearing away too far before the gybe (sailing by the lee for too long): risk of an accidental gybe.",
      "On a heavy-air gybe, rig a preventer rather than relying on the mainsheet alone.",
    ],
    course: "NSS L2",
  },
  {
    id: "reefing",
    name: "Reefing (slab reef)",
    summary:
      "Shorten sail before the wind gets too much. Slab reefing on a yacht: drop the main to the next reef cringle and tie down.",
    whenToUse:
      "Forecast Force 5+, gusts above 25 kt, sustained heeling > 25°, or anything that has you thinking 'should we reef?' — by which point you should already be reefing.",
    steps: [
      {
        actor: "both",
        callout: "Ready to reef!",
        action:
          "Head into the wind or heave-to — take pressure off the sail before you touch the halyards.",
      },
      {
        actor: "crew",
        callout: "",
        action:
          "Slacken the kicker / vang and the mainsheet so the boom can come down.",
      },
      {
        actor: "crew",
        callout: "",
        action:
          "Ease the main halyard until the reef cringle on the luff reaches the gooseneck. Hook the cringle onto the reef hook.",
      },
      {
        actor: "crew",
        callout: "",
        action: "Re-tension the main halyard tight.",
        reason: "A baggy luff sets up sail-flogging vibration that destroys the headboard.",
      },
      {
        actor: "crew",
        callout: "",
        action:
          "Pull the leech reef line through the cringle and winch it down hard — the new clew is level with the boom.",
      },
      {
        actor: "crew",
        callout: "",
        action:
          "Tie the bunt of the sail to the boom using the reef points — bunting passed UNDER the foot of the sail, not round the boom (or you'll tear the sail).",
      },
      {
        actor: "helm",
        callout: "",
        action: "Bear away, sheet in, settle on course.",
      },
    ],
    pitfalls: [
      "Reefing too late — once the boat is on its ear, every step is harder + slower.",
      "Reef points tied around the boom rather than under the foot: when shaken out, the sail tears.",
      "Not tensioning the leech reefing line enough — the foot of the reefed sail sags and acts as a giant flogging bag.",
      "Forgetting to ease the kicker before lowering the halyard — the boom won't come down properly.",
    ],
    course: "Comp Crew",
  },
  {
    id: "heaving-to",
    name: "Heaving-to (the manoeuvre) → hove-to (the resulting state)",
    summary:
      "Stop the boat dead in the water with the sails balanced against the helm. Heaving-TO is the verb — what you do. HOVE-TO is the state the boat sits in afterwards. (\"We're hove-to off the Needles\" = the boat has been heaved-to and is now sitting balanced.)",
    whenToUse:
      "When you need to stop without anchoring — to reef, navigate, deal with seasickness, eat a meal, or wait for a tidal gate. In storm conditions, lying hove-to is a tactic for riding out heavy weather.",
    steps: [
      {
        actor: "helm",
        callout: "Stand by to heave-to!",
        action: "Brief the crew on what's coming — a tack without re-trimming the jib.",
      },
      {
        actor: "helm",
        callout: "Lee-oh!",
        action: "Tack the boat as normal, BUT DO NOT release the working jib sheet.",
        reason: "The jib stays sheeted to the (now-windward) side — it's 'backed'.",
      },
      {
        actor: "crew",
        callout: "",
        action:
          "Once the boat is through the wind, the backed jib pushes the bow away from the wind. Ease the mainsheet so the main flogs.",
      },
      {
        actor: "helm",
        callout: "",
        action:
          "Lash the tiller (or fix the wheel) to leeward — the rudder is trying to push the bow up while the jib is pushing it down. They balance.",
        reason: "The opposing forces stall the boat at ~50° off the wind, drifting downwind very slowly.",
      },
      {
        actor: "both",
        callout: "",
        action:
          "Boat is now HOVE-TO. Lying ~50° off the wind, fore-reaching gently, drifting 1-2 kt downwind + downwave. You can leave the helm, brew up, take a sight, work out a fix, or ride out a blow. Check position regularly — you're still moving.",
        reason: "\"Heaving-to\" is the verb (what you did to get here). \"Hove-to\" is the state (what the boat is doing now).",
      },
    ],
    pitfalls: [
      "Backed jib released by reflex: the boat re-trims and you're sailing again.",
      "Main not eased: the main overpowers the backed jib, the boat keeps sailing slowly forward.",
      "Heavy weather + a heavily-reefed main: many yachts heave-to better under headsail alone (storm jib).",
      "Drifting into shallows or shipping lanes: lie hove-to with plenty of sea room, ideally with a tidal vector taking you to safety.",
      "Forgetting the boat is still moving: while hove-to you typically drift 1-2 kt downwind. Fix your position regularly — a lee shore is the most common hove-to hazard.",
    ],
    course: "Day Skipper",
  },
  {
    id: "capsize-recovery",
    name: "Capsize recovery (dinghy)",
    summary:
      "RYA scoop method — crew is scooped back into the boat as the helm pulls it upright. Stay with the boat.",
    whenToUse:
      "Standard dinghy capsize. Two-person crew. The first rule is: stay with the boat, account for everyone, then recover.",
    steps: [
      {
        actor: "both",
        callout: "",
        action:
          "Surface, check each other is OK + above water. STAY WITH THE BOAT — it floats; you can hold on; rescue cover is more likely to see a boat than a head.",
      },
      {
        actor: "crew",
        callout: "",
        action:
          "Climb onto the centreboard (or daggerboard). Don't pull on the gunwale — you'll inverted-capsize.",
        reason: "Leverage from the board pulls the rig out of the water; pulling on the gunwale pushes the rig deeper.",
      },
      {
        actor: "helm",
        callout: "",
        action:
          "Stay in the water near the cockpit, hold the toe-strap or the centre thwart.",
      },
      {
        actor: "crew",
        callout: "",
        action:
          "Lean back on the centreboard, weight close to the hull. Boat starts to come up.",
      },
      {
        actor: "helm",
        callout: "",
        action:
          "As the boat comes upright, the helm is SCOOPED into the cockpit by the rising hull. No swim required.",
      },
      {
        actor: "crew",
        callout: "",
        action:
          "Crew climbs in over the windward side once helm is in. Sails are released; boat is head-to-wind and stopped.",
      },
      {
        actor: "both",
        callout: "",
        action:
          "Sort the sheets, bail, settle, sail away. If the boat fully inverts, two of you on the centreboard together — same principle.",
      },
    ],
    pitfalls: [
      "Pulling on the gunwale instead of climbing the centreboard: you push the rig deeper, often into a full inversion.",
      "Letting the helm + crew separate from the boat: the boat blows downwind faster than you can swim.",
      "Forgetting to release the sheets before righting: the boat re-fills with wind and capsizes again.",
      "Sailing away with the centreboard half up: weather helm + the boat is unbalanced.",
    ],
    course: "NSS L2",
  },
]);

export const MANOEUVRE_IDS: readonly ManoeuvreId[] = SAIL_MANOEUVRES.map(
  (m) => m.id,
);
